import { useCookies } from '@fergdigitalcommerce/fergy-react-components';
import { type FunctionComponent, type HTMLAttributes } from 'react';
import { type Maybe, type ProductReplacementModelFieldsFragment } from '../../../__generated__/graphql-client-types';
import { COOKIE_LAST_SEARCH } from '../../../constants/cookies';
import { formatPrice } from '../../../helpers/general-helper/general-helper';
import { type ProductCollection } from '../../../types/product.types';
import { Link } from '../../common-components/link/link.component';
import { StyledLink } from '../../common-components/link/styled-link.component';
import { CloudinaryImage } from '../../images/cloudinary-image/cloudinary-image.component';

export type ReplacementModelProps = ProductReplacementModelFieldsFragment & Pick<HTMLAttributes<HTMLElement>, 'className'>;

/**
 * Replacement Model Component
 */
const ReplacementModelComponent: FunctionComponent<ReplacementModelProps> = ({
	className = 'ba br2 b--theme-grey-light f6',
	description,
	price,
	link,
	image
}) => {
	return (
		<li className={`flex flex-column w-100 w-30-m w5-l ma1 lh-copy ${className}`}>
			<Link url={link.url} className="flex flex-column h-100 pa3" color={'grey-darker'}>
				<div className="mv2">
					<CloudinaryImage
						className="h-100 mw-none"
						bypassLazyLoad={true}
						description={image.description}
						publicID={image.id}
						options={{ type: image.imageType, height: 60, width: undefined, crop: 'lpad' }}
					/>
				</div>
				<strong className="db mt2 theme-grey">Replacement Model</strong>
				<span className="mt2 font-medium">{link.name}</span>
				<p className={`mv0`}>
					<span className="tc2-title">{description}</span>
				</p>
				<div className="flex flex-column justify-end h-100 mt2">
					<strong>{formatPrice(price)}</strong>
				</div>
			</Link>
		</li>
	);
};

export type ReplacementsListProps = {
	className?: string;
	replacements: ProductReplacementModelFieldsFragment[];
};

/**
 * Replacements List Component
 *
 * @param {ReplacementsListProps} props
 */
const ReplacementsListComponent: FunctionComponent<ReplacementsListProps> = ({ className, replacements }) => {
	return (
		<ul className={`list flex flex-row flex-wrap flex-auto ma0 ph1 ${className}`.trim()}>
			{replacements.map((model, index) => (
				<ReplacementModelComponent key={index} {...model} />
			))}
		</ul>
	);
};

export type DiscontinuedBuyBoxProps = {
	manufacturer: string;
	replacements?: ProductReplacementModelFieldsFragment[];
	collection?: Maybe<ProductCollection>;
} & Pick<HTMLAttributes<HTMLElement>, 'className'>;

/**
 * Discontinued BuyBox Component
 *
 * @param {DiscontinuedBuyBoxProps} props
 */
export const DiscontinuedBuyBox: FunctionComponent<DiscontinuedBuyBoxProps> = ({
	className = '',
	manufacturer,
	collection,
	replacements
}) => {
	const { getCookie } = useCookies();
	const lastSearch = getCookie(COOKIE_LAST_SEARCH);

	return (
		<section className={`flex flex-column w-100 f6 theme-grey-darker ${className}`.trim()}>
			<h2 className={`mt0 theme-error`}>Discontinued</h2>
			{(collection || lastSearch) && (
				<ul className="flex flex-column mv0 ml4 pa0">
					{collection && (
						<li className="mv1">
							<StyledLink url={`${collection.url}`}>
								Shop All {manufacturer} {collection.name} Collection Products
							</StyledLink>
						</li>
					)}
					{lastSearch && (
						<li className="mv1">
							<StyledLink url={`${lastSearch}`}>Return to last search results</StyledLink>
						</li>
					)}
				</ul>
			)}
			{replacements && replacements.length > 0 ? <ReplacementsListComponent className="mt2" replacements={replacements} /> : null}
			<p className="mt2">We still have product details and accessories, scroll down to see them!</p>
		</section>
	);
};
